import React from 'react';

import { hasFeesInSection } from '../../utils';
import type { FeeSection } from 'app/types/costAndFees.type';
import { useCostsAndFeesContext } from '../../context/CostsAndFeesProvider';
import FeeItem from '../FeeItem';
import {
  DisclaimerText,
  FeesContainerWrapper,
  FeesSection,
  Footer,
  FooterPrice,
  HeaderTitle,
  PriceGroup,
  SectionTitle,
  TitleContainer,
} from './styles';

export default function CostsBreakdown() {
  const { calculatedCostsAndFees, getDisplayAmount } = useCostsAndFeesContext();

  if (!calculatedCostsAndFees || !calculatedCostsAndFees.fees || !calculatedCostsAndFees.costs) {
    return null;
  }

  const { fees, costs } = calculatedCostsAndFees;

  // Define the order we want to display the fee sections
  const orderedFrequencies = ['monthly', 'moveIn', 'whenUsed'];

  const renderFeeSection = (feeFrequency: string, feeSection: FeeSection) => {
    const isAdditionalFees = feeFrequency === 'whenUsed';

    return (
      <React.Fragment key={feeFrequency}>
        <FeesContainerWrapper>
          <TitleContainer>
            <HeaderTitle>{feeSection.displayName}</HeaderTitle>
            {isAdditionalFees && <DisclaimerText>You may be charged additional fees as incurred.</DisclaimerText>}
          </TitleContainer>
          <FeesSection data-testid="fees-section">
            {feeSection.feeGroup?.map((feeGroup, index) => {
              if (isAdditionalFees && feeGroup.type === 'UNGROUPED') {
                return (
                  <React.Fragment key={`${feeFrequency}-${feeGroup.type}-${index}`}>
                    {feeGroup.fees?.map((fee) => (
                      <PriceGroup key={`fee-${fee.displayName}`}>
                        <FeeItem fee={fee} key={fee.displayName} />
                      </PriceGroup>
                    ))}
                  </React.Fragment>
                );
              }

              return feeGroup.displayName ? (
                <PriceGroup key={`${feeFrequency}-${feeGroup.type}-${feeGroup.displayName}`}>
                  <SectionTitle>{feeGroup.displayName}</SectionTitle>
                  {feeGroup.fees?.map((fee) => (
                    <FeeItem fee={fee} key={fee.displayName} />
                  ))}
                </PriceGroup>
              ) : null;
            })}
          </FeesSection>
          {costs[feeFrequency as keyof typeof costs]?.displayName && (
            <Footer>
              <span>{costs[feeFrequency as keyof typeof costs]?.displayName}</span>
              <FooterPrice>
                {getDisplayAmount?.(calculatedCostsAndFees.costs[feeFrequency as keyof typeof costs]?.amount)}
              </FooterPrice>
            </Footer>
          )}
        </FeesContainerWrapper>
      </React.Fragment>
    );
  };

  return (
    <div>
      {orderedFrequencies.map((feeFrequency) => {
        const feeSection = fees[feeFrequency as keyof typeof fees];
        return feeSection && hasFeesInSection(feeSection) && renderFeeSection(feeFrequency, feeSection);
      })}
    </div>
  );
}
